<template>
  <div
    :class="[
      'justify-center start-quote-stepper bg-primary-banner-gradient',
      isMobile ? 'column items-center q-pb-lg' : 'row items-end q-pb-xl',
      hasAcceptedQuote ? 'q-pt-lg' : 'q-pt-xl',
    ]"
  >
    <div class="col"></div>
    <div
      v-if="hasAcceptedQuote"
      :style="{ 'max-width': maxWidth }"
      :class="['col-grow', { 'q-px-md': isMobile }]"
    >
      <div class="row justify-center q-py-lg" v-if="loadingQuote">
        <SAISpinner />
      </div>

      <div v-else>
        <p class="text-weight-medium text-white q-mb-md">
          {{
            hasCreditCheck
              ? hasCompletedCreditCheck
                ? 'Congratulations, your finance has been approved! We are waiting for your employer to approve your quote.'
                : hasSubmittedCreditCheck
                ? 'Your finance application has been submitted and we are waiting on approval.'
                : "It shouldn't be long now before you're driving away in your new car."
              : "You've accepted the vehicle quote."
          }}
        </p>
        <SAIQuoteCard
          :tag="getQuoteTagDetails(acceptedQuote.status).label"
          :tag-color="getQuoteTagDetails(acceptedQuote.status).color"
          :kms-per-year="acceptedQuote.kmsPerYear"
          :lease-term="acceptedQuote.leaseTerm"
          next-text="View Quote"
          :description-ellipsis="false"
          :cost="acceptedQuote.novated.impactTakeHome"
          :cost-from-frequency="
            $Constants.CALCULATION_FREQUENCIES.monthly.value
          "
          :cost-to-frequency="$Constants.CALCULATION_FREQUENCIES.weekly.value"
          :img="
            acceptedQuote.variant.photos.length
              ? acceptedQuote.variant.photos[0].path
              : undefined
          "
          :title="`${
            isMiles(acceptedQuote)
              ? acceptedQuote.variant.name
              : `${acceptedQuote.make.description} ${acceptedQuote.model.name} ${acceptedQuote.variant.name}`
          }`"
          :reference="`${
            isMiles(acceptedQuote)
              ? `${acceptedQuote.milesQuoteRef}/${acceptedQuote.quoteRevisionNumber}`
              : acceptedQuote.erpQuoteRef
          }`"
          @click="$emit('open:quote-summary')"
        />

        <QSeparator
          horizontal
          color="white"
          v-if="hasCreditCheck"
          class="q-mt-lg"
        />

        <h4 class="text-weight-medium text-white q-mt-lg text-center">
          {{ postAcceptedActionText }}
        </h4>

        <div
          v-if="!hasCreditCheck"
          class="full-width rounded-borders-lg bg-white block-pointer q-px-md q-py-lg q-mt-lg"
        >
          <p class="text-center">
            You can complete your finance application online in a matter of
            minutes!
          </p>
          <div class="row justify-center">
            <SAIBtn
              class="q-mt-md"
              outline
              no-caps
              padding="14px lg md"
              @click="$emit('continue:application')"
            >
              <p class="text-weight-medium">Apply for finance</p>
            </SAIBtn>
          </div>
        </div>

        <div class="row justify-center q-mt-lg" v-if="hasCreditCheck">
          <SAIBtn
            color="white"
            outline
            no-caps
            padding="14px lg md"
            @click="$emit('continue:application')"
          >
            <p class="text-weight-medium">Continue Application</p>
          </SAIBtn>
        </div>
      </div>
    </div>

    <div v-else :style="{ 'max-width': maxWidth }" class="col-grow">
      <h4
        class="text-weight-medium text-white text-center q-mb-sm"
        v-if="title"
      >
        {{ title }}
      </h4>

      <SAITabsHeader
        v-if="open"
        :tabs="tabs"
        :is-mobile="isMobile"
        class="tabs-header q-mt-sm"
        size="32px"
        wrap-words
        :tab-padding="isMobile ? '16px 8px 8px' : '16px 18px'"
        background="transparent"
        hide-inset-background
      />

      <div
        :class="[
          'bg-white q-mx-md q-pa-lg q-mt-sm rounded-borders-lg',
          { 'q-mb-lg': isMobile },
        ]"
        v-if="open"
      >
        <div v-if="!hasStarted">
          <p class="text-center">
            Use our calculator to find out how much you could save and just how
            affordable your next car could be.
          </p>
          <div class="row justify-center">
            <SAIBtn
              class="q-mt-md"
              outline
              padding="16px 32px"
              no-caps
              :disable="blockCreateQuote"
              @click="$emit('open:quote')"
            >
              <p class="text-weight-medium">Calculate now</p>
            </SAIBtn>
          </div>
        </div>

        <div v-if="hasStarted && !hasCarSearch">
          <p class="text-center">
            Select the exact car and accessories you want and we'll get you a
            great quote.
          </p>
          <div class="row justify-center">
            <SAIBtn
              class="q-mt-md"
              outline
              padding="16px 32px"
              no-caps
              @click="$emit('open:quote')"
            >
              <p class="text-weight-medium">Select my car</p>
            </SAIBtn>
          </div>
        </div>

        <div v-if="hasCarSearch && !hasSubmission">
          <p class="text-center">
            Select the exact car and accessories you want and we'll get you a
            great quote.
          </p>
          <div class="row justify-center">
            <SAIBtn
              class="q-mt-md"
              outline
              padding="16px 32px"
              no-caps
              @click="$emit('open:quote')"
            >
              <p class="text-weight-medium">Continue</p>
            </SAIBtn>
          </div>
        </div>
      </div>
    </div>
    <div
      :class="[
        'row col',
        {
          'justify-center q-mt-sm': isMobile,
          'items-end justify-end q-px-lg': !isMobile && displayClose,
        },
      ]"
    >
      <SAIBtn no-caps @click="$emit('set:open', !open)" v-if="displayClose">
        <span class="text-weight-medium">{{ open ? 'Close' : 'Open' }}</span>
      </SAIBtn>
    </div>
  </div>
</template>

<script>
import isMiles from '@/utils/is-miles';
import {
  SAIBtn,
  SAIQuoteCard,
  SAISpinner,
  SAITabsHeader,
} from 'ecx.saigon.ui.common';
import { QSeparator } from 'quasar';
import getQuoteTagDetails from '../../../utils/get-quote-tag-details';

export default {
  name: 'QuoteStepper',
  props: {
    isMobile: Boolean,
    maxWidth: String,
    title: String,
    postAcceptedActionText: String,
    open: Boolean,
    tabs: {
      type: Array,
      default: () => [],
    },
    hasStarted: Boolean,
    hasCarSearch: Boolean,
    hasSubmission: Boolean,
    displayClose: Boolean,
    hasAcceptedQuote: Boolean,
    hasCreditCheck: Boolean,
    hasSubmittedCreditCheck: Boolean,
    hasCompletedCreditCheck: Boolean,
    hasSentHrEmail: Boolean,
    loadingQuote: Boolean,
    blockCreateQuote: Boolean,
    acceptedQuote: {
      type: Object,
      default: () => ({}),
    },
  },
  methods: {
    getQuoteTagDetails,
    isMiles,
  },
  components: {
    SAITabsHeader,
    SAIBtn,
    SAIQuoteCard,
    SAISpinner,
    QSeparator,
  },
};
</script>

<style lang="sass" scoped>
.start-quote-stepper
  border-bottom-left-radius: 24px
  border-bottom-right-radius: 24px

.tabs-header::v-deep .bg-white::after
  content: ''
  width: 16px
  height: 16px
  background: white
  position: absolute
  top: calc(100% + 16px)
  transform: rotate(45deg)

.block-pointer
  position: relative

  &::after
    content: ''
    width: 16px
    height: 16px
    background: white
    position: absolute
    left: 0
    right: 0
    top: -8px
    margin: auto
    transform: rotate(45deg)
</style>
